import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL !== undefined
  ? process.env.REACT_APP_BACKEND_URL
  : (typeof window !== 'undefined' && window.location.origin ? window.location.origin : 'http://localhost:8000');
export const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/+$/, '')}/api` : '/api';

const ADMIN_TOKEN = 'gamex_admin_token';
const USER_TOKEN = 'gamex_user_token';

export function getAdminToken() { return localStorage.getItem(ADMIN_TOKEN); }
export function setAdminToken(t) { t ? localStorage.setItem(ADMIN_TOKEN, t) : localStorage.removeItem(ADMIN_TOKEN); }
export function getUserToken() { return localStorage.getItem(USER_TOKEN); }
export function setUserToken(t) { t ? localStorage.setItem(USER_TOKEN, t) : localStorage.removeItem(USER_TOKEN); }

// Tenant host: hostname of the page, allows multi-tenant resolution by header
// even when the backend lives on a different domain (preview / shared infra).
function tenantHost() {
  try {
    const override = localStorage.getItem('gamex_tenant_override');
    if (override) return override;
    return window.location.hostname || '';
  } catch { return ''; }
}

function attachHost(cfg) {
  cfg.headers = cfg.headers || {};
  cfg.headers['x-tenant-host'] = tenantHost();
  return cfg;
}

// Admin API - uses admin token
const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((cfg) => {
  const t = getAdminToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return attachHost(cfg);
});
// Notify the user when their session gets kicked by a login from another device.
// We rely on `setTimeout` so this fires after React finishes the current render.
let _sessionKickedShown = false;
function handleSessionKicked(detail) {
  if (_sessionKickedShown) return;
  const msg = (detail || '').toLowerCase();
  if (!msg.includes('signed in from another device') && !msg.includes('session expired')) return;
  _sessionKickedShown = true;
  setTimeout(() => {
    try {
      toast.error('You were signed out because your account was logged in on another device.', { duration: 6000 });
    } catch { /* ignore */ }
    // Give the toast a moment to render before we bounce the user out
    setTimeout(() => { window.location.reload(); }, 1200);
  }, 0);
}

adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      setAdminToken(null);
      handleSessionKicked(err.response?.data?.detail);
    }
    return Promise.reject(err);
  }
);

// User API - uses user token
const userApi = axios.create({ baseURL: API });
userApi.interceptors.request.use((cfg) => {
  const t = getUserToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return attachHost(cfg);
});
userApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      setUserToken(null);
      handleSessionKicked(err.response?.data?.detail);
    }
    return Promise.reject(err);
  }
);

// Public (still sends host header so tenant resolution works for /site-config)
const pub = axios.create({ baseURL: API });
pub.interceptors.request.use((cfg) => attachHost(cfg));

// Public
export async function fetchSiteConfig() {
  const { data } = await pub.get('/site-config');
  return {
    data: data.data,
    tenant: data.tenant,
    wallet_user_enabled: data.wallet_user_enabled,
    wallet_admin_enabled: data.wallet_admin_enabled,
    deposit_enabled: data.deposit_enabled,
  };
}

// Admin auth
export async function adminLogin(username, password) {
  const { data } = await pub.post('/admin/login', { username, password });
  setAdminToken(data.access_token);
  return data;
}
export async function adminMe() { const { data } = await adminApi.get('/admin/me'); return data; }
export async function updateSiteConfig(payload) { const { data } = await adminApi.put('/admin/site-config', payload); return data.data; }
export async function resetSiteConfigRemote(password) { const { data } = await adminApi.post('/admin/site-config/reset-with-password', { password }); return data.data; }
export async function fetchLeads() { const { data } = await adminApi.get('/admin/leads'); return data.leads; }
export async function fetchStats() { const { data } = await adminApi.get('/admin/stats'); return data; }
export async function changeAdminPassword(current_password, new_password) {
  const { data } = await adminApi.post('/admin/change-password', { current_password, new_password });
  return data;
}
export function leadsCsvUrl() { return `${API}/admin/leads/export`; }
export async function downloadLeadsCsv() {
  const res = await adminApi.get('/admin/leads/export', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url; a.download = `leads-${Date.now()}.csv`; a.click();
  window.URL.revokeObjectURL(url);
}

// Super Admin: tenants
export async function listTenants() { const { data } = await adminApi.get('/admin/tenants'); return data.tenants; }
export async function createTenantAdmin(body) { const { data } = await adminApi.post('/admin/tenants/create-admin', body); return data; }
export async function updateTenantDomains(tid, domains) { const { data } = await adminApi.put(`/admin/tenants/${tid}/domains`, { domains }); return data; }
export async function deleteTenant(tid) { const { data } = await adminApi.delete(`/admin/tenants/${tid}`); return data; }
export async function deleteSubAdmin(username) { const { data } = await adminApi.delete(`/admin/admins/${encodeURIComponent(username)}`); return data; }
export async function createOnboardingLink(tid) { const { data } = await adminApi.post(`/admin/tenants/${tid}/onboarding-link`); return data; }
export async function toggleTenantWallet(tid, scope, enabled) { const { data } = await adminApi.post(`/admin/tenants/${tid}/wallet-toggle`, { scope, enabled }); return data; }
export async function getMyTenantCredits() { const { data } = await adminApi.get('/admin/tenant-credits'); return data; }
export async function resetSubAdminPassword(username, password) { const { data } = await adminApi.post(`/admin/sub-admins/${encodeURIComponent(username)}/reset-password`, { password }); return data; }

// Users (admin/super)
export async function listUsers(tenant_id) {
  const q = tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : '';
  const { data } = await adminApi.get(`/admin/users${q}`);
  return data;
}
export async function deleteAppUser(user_id) { const { data } = await adminApi.delete(`/admin/users/${user_id}`); return data; }

// Onboarding (public)
export async function getOnboardingInfo(token) { const { data } = await pub.get(`/onboarding/${token}`); return data; }
export async function setOnboardingPassword(token, password) { const { data } = await pub.post(`/onboarding/${token}/set-password`, { password }); setAdminToken(data.access_token); return data; }

// === Staff
export async function listStaff() { const { data } = await adminApi.get('/admin/staff'); return data.staff; }
export async function createStaff(body) { const { data } = await adminApi.post('/admin/staff', body); return data; }
export async function updateStaffApi(username, body) { const { data } = await adminApi.put(`/admin/staff/${encodeURIComponent(username)}`, body); return data; }
export async function deleteStaff(username) { const { data } = await adminApi.delete(`/admin/staff/${encodeURIComponent(username)}`); return data; }

// === Payment methods + Service prices
export async function getPaymentMethods() { const { data } = await adminApi.get('/admin/payment-methods'); return data.data; }
export async function savePaymentMethods(body) { const { data } = await adminApi.put('/admin/payment-methods', body); return data; }
export async function getServicePrices() { const { data } = await adminApi.get('/admin/service-prices'); return data.services; }
export async function saveServicePrices(services) { const { data } = await adminApi.put('/admin/service-prices', { services }); return data; }

// === Deposits (admin)
export async function listDeposits(status) {
  const { data } = await adminApi.get(`/admin/wallet/deposits${status ? `?status=${status}` : ''}`);
  return data.deposits;
}
export async function approveDeposit(id) { const { data } = await adminApi.post(`/admin/wallet/deposits/${id}/approve`); return data; }
export async function rejectDeposit(id, note) { const { data } = await adminApi.post(`/admin/wallet/deposits/${id}/reject`, { note }); return data; }

// === AI Config
export async function getAiConfig() { const { data } = await adminApi.get('/admin/ai-config'); return data; }
export async function saveAiConfig(body) { const { data } = await adminApi.put('/admin/ai-config', body); return data; }

// === Chat (admin)
export async function listChatThreads({ includeClosed = false } = {}) { const { data } = await adminApi.get('/admin/chat/threads', { params: { include_closed: includeClosed ? 1 : 0 } }); return data.threads; }
export async function listChatMessages(threadId) { const { data } = await adminApi.get(`/admin/chat/threads/${threadId}/messages`); return data.messages; }
export async function sendChatMessage(body) { const { data } = await adminApi.post('/admin/chat/send', body); return data; }
export async function getChatUnreadSummary() { const { data } = await adminApi.get('/admin/chat/unread-summary'); return data; }
export async function closeChatThread(threadId) { const { data } = await adminApi.post(`/admin/chat/threads/${threadId}/close`); return data; }
export async function reopenChatThread(threadId) { const { data } = await adminApi.post(`/admin/chat/threads/${threadId}/reopen`); return data; }

// === User wallet
export async function getUserWallet() { const { data } = await userApi.get('/user/wallet'); return data; }
export async function userUploadScreenshot(file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await userApi.post('/user/wallet/upload-screenshot', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}
export async function userDeposit(body) { const { data } = await userApi.post('/user/wallet/deposit', body); return data; }
export async function userWalletHistory() { const { data } = await userApi.get('/user/wallet/history'); return data; }
export async function userBuyService(service_id, base_amount) { const { data } = await userApi.post('/user/wallet/buy-service', { service_id, base_amount }); return data; }
export async function userBuyItem(item_type, item_id) { const { data } = await userApi.post('/user/wallet/buy-item', { item_type, item_id }); return data; }

// Media uploads
export async function uploadMedia(file, onProgress) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await adminApi.post('/admin/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    }
  });
  return data; // { ok, url, size, content_type }
}

// Super Admin: wallet
export async function walletAdd(tenant_id, amount, note) { const { data } = await adminApi.post('/admin/wallet/add', { tenant_id, amount, note }); return data; }
export async function walletDeduct(tenant_id, amount, note) { const { data } = await adminApi.post('/admin/wallet/deduct', { tenant_id, amount, note }); return data; }
export async function walletHistory(tid) { const { data } = await adminApi.get(`/admin/wallet/history/${tid}`); return data.history; }

// Admin: bot config
export async function getBotConfig() { const { data } = await adminApi.get('/admin/bot-config'); return data; }
export async function getBotWelcome() { const { data } = await adminApi.get('/admin/bot-welcome'); return data; }
export async function updateBotWelcome(cfg) { const { data } = await adminApi.put('/admin/bot-welcome', cfg); return data; }
export async function updateBotConfig(bot_token) { const { data } = await adminApi.put('/admin/bot-config', { bot_token }); return data; }

// Admin: OTP channel toggles (per-tenant)
export async function getAdminOtpChannels() { const { data } = await adminApi.get('/admin/otp-channels'); return data; }
export async function updateAdminOtpChannels(enabled) { const { data } = await adminApi.put('/admin/otp-channels', { enabled }); return data; }

// Admin: messaging provider credentials (per-tenant)
export async function getAdminMessagingConfig() { const { data } = await adminApi.get('/admin/messaging-config'); return data; }
export async function updateAdminMessagingConfig(payload) { const { data } = await adminApi.put('/admin/messaging-config', payload); return data; }
export async function testAdminMessagingConfig(payload) { const { data } = await adminApi.post('/admin/messaging-config/test', payload); return data; }

// Admin: purchases (delivery tracking)
export async function adminListPurchases(status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  const { data } = await adminApi.get(`/admin/purchases${q}`);
  return data.purchases;
}
export async function adminSetPurchaseStatus(pid, status, note = '') {
  const { data } = await adminApi.put(`/admin/purchases/${pid}/status`, { status, note });
  return data;
}

// User: my purchases (delivery status visible to buyer)
export async function userPurchases() { const { data } = await userApi.get('/user/purchases'); return data.purchases; }

// Public reviews + user submit
export async function listReviews(item_type, item_id) {
  const { data } = await pub.get(`/reviews/${item_type}/${item_id}`);
  return data;
}
export async function topRatedItems(min_rating = 4.5, limit = 12) {
  const { data } = await pub.get(`/reviews/top-rated?min_rating=${min_rating}&limit=${limit}`);
  return data.items;
}
export async function getMyReview(item_type, item_id) {
  const { data } = await userApi.get(`/user/reviews/mine/${item_type}/${item_id}`);
  return data.review;
}
export async function postReview(body) { const { data } = await userApi.post('/user/reviews', body); return data; }
export async function adminListReviews() { const { data } = await adminApi.get('/admin/reviews'); return data.reviews; }
export async function adminUpdateReview(rid, body) { const { data } = await adminApi.put(`/admin/reviews/${rid}`, body); return data; }
export async function adminDeleteReview(rid) { const { data } = await adminApi.delete(`/admin/reviews/${rid}`); return data; }

// Admin: deposit toggle (self, per-tenant)
export async function getDepositToggle() { const { data } = await adminApi.get('/admin/deposit-toggle'); return data; }
export async function setDepositToggle(enabled) { const { data } = await adminApi.put('/admin/deposit-toggle', { enabled }); return data; }

// Admin: quick replies (chat)
export async function getQuickReplies() { const { data } = await adminApi.get('/admin/quick-replies'); return data.replies; }
export async function saveQuickReplies(replies) { const { data } = await adminApi.put('/admin/quick-replies', { replies }); return data.replies; }

// User auth (password)
export async function userRegister(body) {
  const { data } = await pub.post('/auth/register', body);
  setUserToken(data.access_token);
  return data;
}
export async function userLogin(body) {
  const { data } = await pub.post('/auth/login', body);
  setUserToken(data.access_token);
  return data;
}
export async function userMe() { const { data } = await userApi.get('/auth/me'); return data.user; }

// OTP — server fan-outs to every channel the tenant admin has enabled
// (WhatsApp + SMS + Telegram deep-link). The user no longer picks a channel.
export async function requestOtp(mobile, purpose = 'signup') {
  const { data } = await pub.post('/auth/request-otp', { mobile, purpose });
  return data;
}
export async function fetchOtpChannels() { const { data } = await pub.get('/auth/otp-channels'); return data; }
export async function verifyOtp({ mobile, code, purpose, name, password }) {
  const { data } = await pub.post('/auth/verify-otp', { mobile, code, purpose, name, password });
  if (data?.access_token) setUserToken(data.access_token);
  return data;
}

// Public enquiry (no auth required)
export async function submitEnquiry(body) {
  const { data } = await pub.post('/enquiry', body);
  return data;
}

export default pub;
